import { IconButton, type PopoverProps, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwimlaneEventPublisher } from '@/common/components/ui/swimlane/api';
import { InternalContext } from '@/common/components/ui/swimlane/context/InternalContext';
import { StyledContextMenu } from '@/common/components/ui/swimlane/context-menu';
import { HEADER_MENU_BUTTON_SIZE } from '@/common/components/ui/swimlane/layout/constants';
import { usePortalContainer } from '@/features/views/components/PortalContainerContext';
import { Default } from './options/Default';
import type { IHeaderMenuProps } from './types';

export function SwimlaneHeaderMenu({
  Icon,
  onOpenCard,
  hideHeader,
  openCardLabel,
  hideHeaderTooltip,
  reorderDisabledTooltip,
  isReOrderingDisabled = false,
  isViewCardOptionDisabled,
  level,
  isGroupHeader,
  parentHeaderId,
  buttonSize = HEADER_MENU_BUTTON_SIZE.value,
}: IHeaderMenuProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<Element | null>(null);
  const { colAxisLabelRef, rowAxisLabelRef } = useContext(InternalContext);
  const portalContainer = usePortalContainer();
  const publishEvent = useSwimlaneEventPublisher();

  const onClose = () => {
    setAnchor(null);
  };

  const anchorOrigin: PopoverProps['anchorOrigin'] =
    level === 1
      ? { vertical: 'bottom', horizontal: 'center' }
      : { vertical: 'center', horizontal: 'right' };

  const openReorderMenu = () => {
    setAnchor(null); // close the menu
    const anchorEl = level === 1 ? colAxisLabelRef.current : rowAxisLabelRef.current;
    if (anchorEl) {
      publishEvent('openReorderHeadersMenu', { anchorEl, level, isGroupHeader, parentHeaderId });
    }
  };

  return (
    <>
      <div style={{ alignSelf: 'end', zIndex: 1000 }}>
        <Tooltip
          title={t(($) => $.views.visualization.header.options)}
          PopperProps={{
            container: portalContainer,
          }}
        >
          <IconButton
            onClick={(e: React.MouseEvent) => setAnchor(e.currentTarget)}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: `${buttonSize}px`,
              height: `${buttonSize}px`,
            }}
          >
            <span data-testid="swimlane-header-context-menu-button">
              <Icon className="size-6" />
            </span>
          </IconButton>
        </Tooltip>
      </div>
      <StyledContextMenu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        MenuListProps={{
          'aria-labelledby': 'header-menu',
        }}
        anchorOrigin={anchorOrigin}
        container={portalContainer}
      >
        <Default
          hideHeader={hideHeader}
          onOpenCard={onOpenCard}
          handleClose={onClose}
          openCardLabel={openCardLabel}
          hideHeaderTooltip={hideHeaderTooltip}
          reorderDisabledTooltip={reorderDisabledTooltip}
          isReOrderingDisabled={isReOrderingDisabled}
          isViewCardOptionDisabled={isViewCardOptionDisabled}
          openReorderMenu={openReorderMenu}
        />
      </StyledContextMenu>
    </>
  );
}
